import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  RadioButton,
  RadioButtonGroup,
  RadioButtonTheme,
} from 'wix-ui-tpa/cssVars';
import { type PaymentOption, BorderStyle } from '../../../../../../types/types';
import { getServiceSlotIdentifier } from '../../../../../../utils';
import { ServicePaymentRadioButtonsHooks } from './dataHooks';
import type { PaymentFormatProps } from './ServicePaymentOptions';
import { classes, st } from './ServicePaymentRadioButtons.st.css';
import settingsParams from '../../../../settingsParams';
import { useSettings } from '@wix/tpa-settings/react';
import { DiscountRuleName } from '../../../Discount/Discount';
import { ErrorSmall } from '@wix/wix-ui-icons-common/on-stage';

export const ServicePaymentRadioButtons: React.FC<PaymentFormatProps> = ({
  service,
  paymentOptions,
  label,
  selectedId,
  onSelect,
  getPaymentOptionLabel,
  buildPaymentOptionId,
}) => {
  const { isMobile } = useEnvironment();
  const isUnderline =
    useSettings().get(settingsParams.fieldsBorderStyle) ===
    BorderStyle.UNDERLINE;
  const theme = isUnderline ? RadioButtonTheme.Default : RadioButtonTheme.Box;

  const getSubtext = (option: PaymentOption): string | undefined =>
    (
      <div>
        {option.discountDetails ? (
          <>
            {option.pricingDetails && <div>{option.pricingDetails}</div>}
            <DiscountRuleName {...option.discountDetails} align="start" />
          </>
        ) : option.disabled ? (
          <div className={classes.disabledDescription}>
            <div>{option.pricingDetails}</div>
            <div>
              <ErrorSmall aria-label={option.disabledReason} role="img" />{' '}
              {option.disabledReason}
            </div>
          </div>
        ) : (
          option.pricingDetails
        )}
      </div>
    ) as any as string;

  const groupName = `payment-options-${getServiceSlotIdentifier(
    service.nestedSlot,
  )}`;

  return (
    <div
      className={classes.root}
      data-hook={`${
        ServicePaymentRadioButtonsHooks.RadioButtons
      }_${getServiceSlotIdentifier(service.nestedSlot)}`}
    >
      <RadioButtonGroup
        label={label}
        className={classes.radioButtonGroup}
        name={groupName}
        value={selectedId}
        onChange={onSelect}
        withSpacing={!isUnderline}
        fluid
        theme={theme}
      >
        {paymentOptions.map((option) => {
          const optionValue = buildPaymentOptionId(option);
          const isDisabled = option.disabled;

          return (
            <RadioButton
              className={st(classes.radioButton, { isMobile, isUnderline })}
              key={optionValue}
              value={optionValue}
              label={getPaymentOptionLabel(option)}
              subtext={getSubtext(option)}
              suffix={option.suffix}
              checked={selectedId === optionValue}
              disabled={isDisabled}
            />
          );
        })}
      </RadioButtonGroup>
    </div>
  );
};
